import type { IStairConfig } from '@stair-craft/common/util';

import { IStairNorms } from '../../norms';

/**
 * Pick `flight2Count` such that `treadDepth = maxFlight2Run / flight2Count` falls
 * within the norm range `[treadDepthMin, treadDepthMax]`, preferring the count
 * whose tread depth is closest to ideal. If no in-norm count exists, falls back to
 * the closest-to-min count (clamping tread depth at the norm edge). Shared by the
 * L-shape and U-shape calculators (byte-identical in both before extraction).
 */
export function pickInNormFlight2Count(maxFlight2Run: number, norms: IStairNorms): number {
    if (maxFlight2Run <= 0) {
        return 1;
    }
    const lo: number = Math.max(1, Math.floor(maxFlight2Run / norms.treadDepthMax));
    const hi: number = Math.max(1, Math.ceil(maxFlight2Run / norms.treadDepthMin));
    let best: number = lo;
    let bestDist: number = Infinity;
    for (let c: number = lo; c <= hi; c++) {
        const tread: number = maxFlight2Run / c;
        const dist: number = Math.abs(tread - norms.treadDepthIdeal);
        if (dist < bestDist) {
            bestDist = dist;
            best = c;
        }
    }
    return best;
}

/** Step split of an L/U turn: total risers and how many treads go to each flight. */
export interface ITurnStepSplit {
    stepCount: number;
    flight1Count: number;
    flight2Count: number;
}

/** Inputs of `pickInNormTurnSplit`; lengths in mm, the angle in degrees. */
export interface ITurnStepSplitInput {
    totalRise: number;
    maxFlight2Run: number;
    turnStepCount: number;
    /** Flight 1 tread count pinned by the user (`turnAtStep`), or null to pick it too. */
    pinnedFlight1Count: number | null;
    /** The angle the user asked for; breaks ties between in-norm splits. Null = closest-to-ideal tread. */
    targetAngle: number | null;
    /**
     * Whether the auto pick may start the stair on the turn, with no flight 1 treads. Winders may:
     * a low floor cannot hold flight 1, six U winders and a flight 2 that spans the opening.
     */
    startOnTurn?: boolean;
}

/** Share of a two-sided norm range kept clear at each edge; mirrors the validator's warning band. */
export const NORM_EDGE_BAND_FRACTION: number = 0.1;

const SCORE_EPSILON: number = 1e-9;

/** How much more a share past the norm range weighs than a share inside its warning band. */
const OUT_OF_RANGE_WEIGHT: number = 10;

/** Added to the preference of a split with no flight 1: larger than any angle or tread distance. */
const START_ON_TURN_HANDICAP: number = 1e6;

function angleDeg(riserHeight: number, treadDepth: number): number {
    return Math.atan2(riserHeight, treadDepth) * (180 / Math.PI);
}

/**
 * How far `value` sits outside the "ok" part of [min, max] (the range minus its warning
 * bands), as a share of the range width. Zero means the validator reports it as ok.
 */
export function outOfNormShare(value: number, min: number, max: number): number {
    const width: number = max - min;
    const band: number = width * NORM_EDGE_BAND_FRACTION;
    const outsideOk: number = Math.max(0, min + band - value, value - (max - band)) / width;
    // Past the range itself the validator reports an error, not a warning: a pick that trades a
    // small error for two warnings opened the L-shape with an angle of 29.6°.
    const outsideRange: number = Math.max(0, min - value, value - max) / width;
    return outsideOk + OUT_OF_RANGE_WEIGHT * outsideRange;
}

/** Sum of the out-of-norm shares of riser, tread, Blondel and angle for one split. */
function splitNormPenalty(riserHeight: number, treadDepth: number, norms: IStairNorms): number {
    return (
        outOfNormShare(riserHeight, norms.riserHeightMin, norms.riserHeightMax) +
        outOfNormShare(treadDepth, norms.treadDepthMin, norms.treadDepthMax) +
        outOfNormShare(2 * riserHeight + treadDepth, norms.blondelMin, norms.blondelMax) +
        outOfNormShare(angleDeg(riserHeight, treadDepth), norms.angleMin, norms.angleMax)
    );
}

/** Tie-break between splits of equal penalty: closeness to the asked angle, else to the ideal tread. */
function splitPreference(riserHeight: number, treadDepth: number, targetAngle: number | null, norms: IStairNorms): number {
    return targetAngle != null ? Math.abs(angleDeg(riserHeight, treadDepth) - targetAngle) : Math.abs(treadDepth - norms.treadDepthIdeal);
}

/** Flight 1 counts worth trying: the pinned one, or every count that keeps the riser near its norm. */
function flight1Candidates(input: ITurnStepSplitInput, flight2Count: number, norms: IStairNorms): number[] {
    if (input.pinnedFlight1Count != null) {
        return [Math.max(0, input.pinnedFlight1Count)];
    }
    const stepLo: number = Math.max(3, Math.floor(input.totalRise / norms.riserHeightMax));
    const stepHi: number = Math.ceil(input.totalRise / norms.riserHeightMin);
    const minFlight1Count: number = input.startOnTurn === true ? 0 : 1;
    const candidates: number[] = [];
    for (let stepCount: number = stepLo; stepCount <= stepHi; stepCount++) {
        const flight1Count: number = stepCount - 1 - input.turnStepCount - flight2Count;
        if (flight1Count >= minFlight1Count) {
            candidates.push(flight1Count);
        }
    }
    return candidates;
}

/**
 * Flight 2 counts worth trying: those whose tread lands inside the tread norm, so the
 * joint pick never trades a too-shallow tread for a better riser. With no in-norm count
 * (a very short or very long run) the whole window is tried and the penalty decides.
 */
function flight2Candidates(maxFlight2Run: number, norms: IStairNorms): number[] {
    const lo: number = Math.max(1, Math.floor(maxFlight2Run / norms.treadDepthMax));
    const hi: number = Math.max(1, Math.ceil(maxFlight2Run / norms.treadDepthMin));
    const all: number[] = [];
    for (let count: number = lo; count <= hi; count++) {
        all.push(count);
    }
    const inNorm: number[] = all.filter((count: number): boolean => {
        const tread: number = maxFlight2Run / count;
        return tread >= norms.treadDepthMin && tread <= norms.treadDepthMax;
    });
    return inNorm.length > 0 ? inNorm : all;
}

/**
 * Pick the step split of an L/U turn jointly. Flight 2's tread is force-fit to
 * `maxFlight2Run / flight2Count` and the riser is `totalRise / stepCount`, so the two
 * counts decide riser, tread, Blondel and angle together. Picking flight 2 alone by the
 * ideal tread left the riser to whatever the turn and flight 1 added up to: with winders
 * that is two (L) or five (U) more risers, and the stair opened with a Blondel error.
 * Every candidate is scored by how far it sits outside the validator's "ok" bands; ties
 * go to the asked angle. With a pinned flight 1 only flight 2 is free.
 *
 * Returns null when there is no run for flight 2 (the caller keeps its fallback).
 */
export function pickInNormTurnSplit(input: ITurnStepSplitInput, norms: IStairNorms): ITurnStepSplit | null {
    if (input.maxFlight2Run <= 0) {
        return null;
    }
    let best: ITurnStepSplit | null = null;
    let bestPenalty: number = Infinity;
    let bestPreference: number = Infinity;
    for (const flight2Count of flight2Candidates(input.maxFlight2Run, norms)) {
        const treadDepth: number = input.maxFlight2Run / flight2Count;
        for (const flight1Count of flight1Candidates(input, flight2Count, norms)) {
            const stepCount: number = Math.max(3, flight1Count + 1 + input.turnStepCount + flight2Count);
            const riserHeight: number = input.totalRise / stepCount;
            const penalty: number = splitNormPenalty(riserHeight, treadDepth, norms);
            // A stair that starts on the turn is taken only when it is closer to the norm: at equal
            // penalty a split with a flight 1 wins, whatever the preference says.
            const preference: number =
                splitPreference(riserHeight, treadDepth, input.targetAngle, norms) + (flight1Count === 0 ? START_ON_TURN_HANDICAP : 0);
            const better: boolean =
                penalty < bestPenalty - SCORE_EPSILON || (penalty <= bestPenalty + SCORE_EPSILON && preference < bestPreference);
            if (better) {
                best = { stepCount, flight1Count, flight2Count };
                bestPenalty = penalty;
                bestPreference = preference;
            }
        }
    }
    return best;
}

/** What the L/U layout has settled before splitting the flights. */
export interface ITurnSplitBase {
    totalRise: number;
    maxFlight2Run: number;
    turnStepCount: number;
    /** Step count resolved from the angle / riser / pinned count, at least two treads past the turn. */
    stepCount: number;
}

/**
 * The split used before `pickInNormTurnSplit`: flight 2 by the ideal tread alone, flight 1
 * from the pinned turnAtStep or from what the step count leaves. Kept for a step count or a
 * riser pinned by the user, and for a turn with no run left for flight 2.
 */
function legacyTurnSplit(config: IStairConfig, norms: IStairNorms, base: ITurnSplitBase): ITurnStepSplit {
    const flight2Picked: number = pickInNormFlight2Count(base.maxFlight2Run, norms);
    if (config.turnAtStep == null) {
        const remainingTreads: number = base.stepCount - 1 - base.turnStepCount;
        return { stepCount: base.stepCount, flight1Count: Math.max(1, remainingTreads - flight2Picked), flight2Count: flight2Picked };
    }
    // turnAtStep === 0 is allowed: the stair starts straight on the winders/landing.
    const requiredStepCount: number = Math.max(0, config.turnAtStep) + 1 + base.turnStepCount + flight2Picked;
    const stepCount: number = Math.max(3, requiredStepCount);
    const remainingTreads: number = stepCount - 1 - base.turnStepCount;
    const flight1Count: number = Math.max(0, Math.min(remainingTreads - 1, config.turnAtStep));
    return { stepCount, flight1Count, flight2Count: Math.max(1, remainingTreads - flight1Count) };
}

/**
 * Split an L/U stair into flight 1, the turn and flight 2. The joint in-norm pick runs
 * when the user pinned the turn step or left the step count and the riser to the
 * calculator; a pinned step count or riser keeps the legacy split, which honours it.
 */
export function resolveTurnSplit(config: IStairConfig, norms: IStairNorms, base: ITurnSplitBase): ITurnStepSplit {
    const free: boolean = config.turnAtStep != null || (config.stepCount == null && config.riserHeight == null);
    const split: ITurnStepSplit | null = free
        ? pickInNormTurnSplit(
              {
                  totalRise: base.totalRise,
                  maxFlight2Run: base.maxFlight2Run,
                  turnStepCount: base.turnStepCount,
                  pinnedFlight1Count: config.turnAtStep,
                  targetAngle: config.stairAngle,
                  startOnTurn: config.turnType === 'winder',
              },
              norms
          )
        : null;
    return split ?? legacyTurnSplit(config, norms, base);
}
